import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ConversationTypeEnum, MessageTypeEnum } from "common/enums";
import { Conversation, Message } from "common/types";
import React, { useDeferredValue, useEffect } from "react";
import MicIcon from "@mui/icons-material/Mic";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useChatStore } from "store/useChatStore";
import dayjs from "dayjs";
import { useShallow } from "zustand/shallow";
import { mapConversationPriorityColor } from "utils/conversations";
import Loading from "components/Loading";
import Badge from "@mui/material/Badge";
import ForumTwoToneIcon from "@mui/icons-material/ForumTwoTone";
import { defaultColorPallete } from "utils";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import VideocamIcon from "@mui/icons-material/Videocam";
import Fuse from "fuse.js";
import { useInView } from "react-intersection-observer";

type Props = {
  onClickConversation: (conversation: Conversation) => void;
  conversationsLoading: boolean;
  searchTerm: string;
  fetchNextPage: () => void;
  fetchPreviousPage: () => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
};

export default function ConversationsList(props: Props) {
  const {
    onClickConversation,
    conversationsLoading,
    searchTerm,
    hasNextPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    fetchNextPage,
  } = props;

  const { conversations, selectedConversation } = useChatStore(
    useShallow((state) => ({
      conversations: state.conversations,
      selectedConversation: state.selectedConversation,
      conversationFilter: state.conversationFilter,
    }))
  );

  const enhancedConversations = conversations.map((c) => {
    const name = c?.messages?.[0]?.contact?.name?.toLowerCase() || "";
    const rawPhone = c?.messages?.[0]?.contact?.phone || "";
    const phone = rawPhone.replace("@s.whatsapp.net", "").replace(/\D/g, "");
    const simplifiedPhone = phone.replace(/^55(\d{2})9?/, "");

    return {
      ...c,
      _search: { name, phone, simplifiedPhone },
    };
  });

  const fuse = new Fuse(enhancedConversations, {
    keys: ["_search.name", "_search.phone", "_search.simplifiedPhone"],
    threshold: 0.3,
  });

  const filteredConversations = searchTerm
    ? fuse.search(searchTerm.toLowerCase()).map((r) => r.item)
    : enhancedConversations;

  const deferredConversations = useDeferredValue(filteredConversations);

  // const { ref: topRef, inView: topInView } = useInView();
  const { ref: bottomRef, inView: bottomInView } = useInView();

  // useEffect(() => {
  //   if (
  //     topInView &&
  //     hasPreviousPage &&
  //     !isFetchingPreviousPage &&
  //     !isFetchingNextPage &&
  //     !conversationsLoading
  //   ) {
  //     fetchPreviousPage();
  //   }
  // }, [
  //   topInView
  // ]);

  useEffect(() => {
    if (
      bottomInView &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isFetchingPreviousPage &&
      !conversationsLoading
    ) {
      fetchNextPage();
    }
  }, [bottomInView]);

  if (conversationsLoading) {
    return <Loading />;
  }

  return (
    <Box
      mt={2}
      height="400px"
      mb={2}
      flexGrow={1}
      overflow="auto"
      gap={1}
      display="flex"
      flexDirection="column"
      pr={{ md: 1 }}
    >
      {deferredConversations?.length <= 0 && (
        <Box display="flex" alignSelf="center">
          <ForumTwoToneIcon sx={{ color: defaultColorPallete.primary.main }} />
          <Typography>Por enquanto não há conversas...</Typography>
        </Box>
      )}

      {/* {hasPreviousPage && (
        <div ref={topRef} style={{ height: 1 }}>
          topView
        </div>
      )} */}
      {deferredConversations?.length > 0 &&
        deferredConversations.map((conversation, index) => {
          const conversationPriorityColor = mapConversationPriorityColor(
            conversation.priority
          ).dark;
          const isBeforePenultimate =
            index === deferredConversations.length - 2;
          return (
            <React.Fragment key={conversation.id}>
              <Box
                bgcolor={
                  selectedConversation?.id === conversation.id
                    ? "#DEEDEF"
                    : "white"
                }
                sx={{ cursor: "pointer" }}
                height={"70px"}
                display="flex"
                width="100%"
                borderRadius="12px"
                overflow="clip"
              >
                <Box
                  flex={1}
                  gap={2}
                  display="flex"
                  onClick={() => onClickConversation(conversation)}
                  height="100%"
                  alignContent="center"
                  p={2}
                >
                  <Avatar
                    src={
                      conversation?.messages[0]?.contact?.imgData
                        ? `data:image/;base64,${conversation?.messages[0]?.contact?.imgData}`
                        : undefined
                    }
                  />
                  <Box flex={1} width="70px">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      style={{
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      {conversation?.messages[0]?.contact?.name ||
                        conversation.name}
                    </Typography>
                    {conversation?.messages[0] &&
                      mapLastMessage(conversation?.messages[0])}
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    width="10%"
                    alignItems="center"
                  >
                    <Typography
                      color={conversationPriorityColor}
                      variant="caption"
                      key={`Conversation-${conversation.id}-lastMessageTime`}
                      fontSize="0.6rem"
                      component="p"
                      textAlign="right"
                      alignContent="center"
                    >
                      {mapTimeLastMessage(
                        new Date(conversation?.messages[0]?.receivedAt)
                      )}
                    </Typography>
                    {conversation?._count?.messages > 0 && (
                      <Badge
                        badgeContent={conversation._count.messages}
                        sx={{
                          "& .MuiBadge-badge": {
                            backgroundColor: conversationPriorityColor,
                            color: "white",
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box
                  width="12px"
                  bgcolor={
                    conversation.type !== ConversationTypeEnum.GROUP
                      ? conversationPriorityColor
                      : "transparent"
                  }
                  height="100%"
                />
              </Box>
              {isBeforePenultimate && hasNextPage && (
                <div
                  key={`ConversationsList-bottomSentinel-${deferredConversations.length}`}
                  ref={bottomRef}
                  style={{ height: 1 }}
                />
              )}
            </React.Fragment>
          );
        })}
      {isFetchingNextPage && <Loading />}
    </Box>
  );
}

function mapTimeLastMessage(dateTime: string | Date): string {
  const d = dayjs(dateTime);

  if (d.isSame(dayjs(), "day")) return d.format("HH:mm");
  if (d.isSame(dayjs().subtract(1, "day"), "day")) return "Ontem";
  if (d.isSame(dayjs().subtract(2, "day"), "day")) return "Anteontem";

  return d.format("DD/MM/YYYY");
}

function mapLastMessage(message: Partial<Message>) {
  switch (message.messageType) {
    case MessageTypeEnum.text:
      return (
        <Typography
          noWrap
          style={{
            textDecoration: "none",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
          fontSize="0.7rem"
        >
          {message.textContent}
        </Typography>
      );
    case MessageTypeEnum.audio:
      return (
        <Box display="flex" alignItems="center">
          <MicIcon fontSize="small" sx={{ fontSize: "0.9rem" }} />
          <Typography fontSize="0.7rem">Audio</Typography>
        </Box>
      );
    case MessageTypeEnum.video:
      return (
        <Box display="flex" alignItems="center">
          <VideocamIcon fontSize="small" sx={{ fontSize: "0.9rem" }} />
          <Typography fontSize="0.7rem">Vídeo</Typography>
        </Box>
      );
    case MessageTypeEnum.image:
      return (
        <Box display="flex" alignItems="center">
          <InsertPhotoIcon fontSize="small" sx={{ fontSize: "0.9rem" }} />
          <Typography fontSize="0.7rem">Imagem</Typography>
        </Box>
      );
    case MessageTypeEnum.document:
      return (
        <Box display="flex" alignItems="center">
          <InsertDriveFileIcon sx={{ fontSize: "0.9rem" }} />
          <Typography fontSize="0.7rem">Arquivo</Typography>
        </Box>
      );
    default:
      return (
        <Box display="flex" alignItems="center">
          <InsertDriveFileIcon sx={{ fontSize: "0.9rem" }} />
          <Typography fontSize="0.7rem">Mensagem Desconhecida</Typography>
        </Box>
      );
  }
}
